import type { ComponentProps } from "react";

import { formatDateTime, formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";

/*
 * The one recipe for a plain data table (lists that are not the record grid):
 * a 28px sticky header in 12px muted medium text with a hairline underneath,
 * 32px rows, no vertical rules, hover tint, and relative times that show the
 * full time on hover. border-separate keeps the header hairline attached to
 * the sticky header while the body scrolls.
 */

export function PlainTable({
  className,
  striped = false,
  ...props
}: ComponentProps<"table"> & { striped?: boolean }) {
  return (
    <table
      className={cn(
        "w-full table-fixed border-separate border-spacing-0 text-sm",
        striped &&
          "[&>tbody>tr:nth-child(even)]:bg-muted/25 [&>tbody>tr:hover]:bg-muted/50",
        className,
      )}
      {...props}
    />
  );
}

export function PlainTableHead({
  className,
  children,
  ...props
}: ComponentProps<"thead">) {
  return (
    <thead
      className={cn("sticky top-0 z-10 bg-background", className)}
      {...props}
    >
      <tr className="text-xs font-medium text-muted-foreground">{children}</tr>
    </thead>
  );
}

export function PlainTh({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      className={cn("h-7 border-b border-border/50 px-3 text-left", className)}
      {...props}
    />
  );
}

export function PlainRow({ className, ...props }: ComponentProps<"tr">) {
  return <tr className={cn("h-8 hover:bg-muted/50", className)} {...props} />;
}

export function PlainTd({ className, ...props }: ComponentProps<"td">) {
  return (
    <td
      className={cn("truncate border-b border-border/50 px-3", className)}
      {...props}
    />
  );
}

export function RelativeTime({
  value,
  className,
}: {
  value?: number | null;
  className?: string;
}) {
  if (!value)
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  return (
    <time
      dateTime={new Date(value).toISOString()}
      title={formatDateTime(value)}
      className={className}
    >
      {formatRelativeTime(value)}
    </time>
  );
}
